<template>
  <q-page class="flex" style="max-height: calc(100vh - 64px)">
    <div class="full-width" style="max-height: calc(100vh - 64px);">
      <div class="max-width-limited q-pl-md q-pr-md flex column no-wrap items-stretch" style="height: 100%">
        <div class="text-center q-pt-md q-pb-md">
          <div class="worker-title">
            {{$t('pages.fix.worker')}}: {{getFullName}}
          </div>
          <div class="q-mt-sm" v-t="'pages.fix.shortInstruction'"></div>
        </div>
        <div class="mirror flex items-center justify-center" ref="mirror">
          <div ref="bgVideo" class="flex items-center justify-center">
            <q-spinner
              color="primary"
              size="4em"
              v-if="!videoStreamReady"
              class="spinner"
            />
          </div>
        </div>
        <div class="flex row justify-center q-pt-lg q-pb-lg">
          <div v-show="controlState === 1">
            <q-btn
              icon-right="photo_camera"
              :label="$t('pages.fix.shootButtonTitle')"
              color="primary"
              size="lg"
              @click="shoot()"
              no-caps
              :loading="dataSending"
            />
          </div>
          <div v-show="controlState === 2" class="flex row no-wrap">
            <q-btn
              icon-right="directions_run"
              :label="$t('pages.fix.coming')"
              color="green"
              size="lg"
              @click="send('C')"
              no-caps
              :loading="dataSending"
              class="q-mr-lg"
            />
            <q-btn
              icon-right="redo"
              :label="$t('pages.fix.reshootButtonTitle')"
              color="grey"
              size="lg"
              class="q-mr-lg"
              @click="reshoot()"
              no-caps
              :disabled="dataSending"
            />
            <q-btn
              icon-right="exit_to_app"
              :label="$t('pages.fix.leaving')"
              color="red"
              size="lg"
              @click="send('L')"
              no-caps
              :loading="dataSending"
            />
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>
<script>
import { API_REQUEST } from '../api.js'
import CamService from './camService'
import { mapState, mapGetters } from 'vuex'
import { markPerfEvent } from '../modules/urv-perf'
let camService

export default {
  name: 'FixWorker',
  data () {
    return {
      controlState: 1,
      videoStreamReady: false,
      dataSending: false,
      tickId: null
    }
  },
  methods: {
    async shoot () {
      this.dataSending = true
      camService.pause()
      camService.hideMask()
      this.$store.commit('setFirstPicsPaused', true)
      if (!this.tickId) {
        try {
          const res = await API_REQUEST('initTick', {
            user_id: this.selectedWorker.user_id,
            employee_id: this.selectedWorker.employee_id,
            tick_point_id: this.tickPoint.id,
            type: 'N'
          })
          this.tickId = res.id
          markPerfEvent('checkin_click', {
            tick_id: this.tickId
          })
        } catch (e) {
          this.showError(e.message)
          this.$router.push('/')
        }
      }
      this.dataSending = false
      this.controlState = 2
    },
    reshoot () {
      this.controlState = 1
      camService.play()
      camService.showMask()
      this.$store.commit('setFirstPicsPaused', false)
    },
    async send (tickType) {
      this.dataSending = true
      try {
        markPerfEvent('self_capture_start', {
          tick_id: this.tickId,
          tick_type: tickType
        })
        const selfImageBlob = await camService.getPicture()
        markPerfEvent('self_capture_done', {
          tick_id: this.tickId
        })
        const res = await API_REQUEST('updateTick', {
          user_id: this.selectedWorker.user_id,
          employee_id: this.selectedWorker.employee_id,
          tick_point_id: this.tickPoint.id,
          type: tickType,
          id: this.tickId
        })
        if (res.detail) {
          this.showError(res)
          this.$router.push('/')
          return
        }
        const currentTickId = res.id
        let answer
        if (currentTickId) {
          const firstPics = this.$store.state.firstPics
          if (!firstPics || !firstPics.length) {
            throw new Error(this.$t('pages.fix.noFirstPicsError'))
          }
          const firstImageBlob = firstPics[0] || firstPics[1] || firstPics[2]
          const storedImage = new File([firstImageBlob], 'camPicFirst.jpg')

          markPerfEvent('self_upload_start', {
            tick_id: currentTickId
          })
          const promise1 = API_REQUEST('setTickPhoto', {
            tick_id: currentTickId,
            type: 'S',
            user_id: this.selectedWorker.user_id, // have to be here!
            tickType, // have to be here!
            image: new File([selfImageBlob], 'camPicSelf.jpg')
          }).then((photoRes) => {
            markPerfEvent('self_upload_done', {
              tick_id: currentTickId,
              status: photoRes.http_status || (photoRes.OFFLINE ? 'offline' : 'unknown')
            })
            return photoRes
          })
          const promise2 = API_REQUEST('setTickPhoto', {
            tick_id: currentTickId,
            type: 'F',
            image: storedImage
          })

          const promisesResults = await Promise.all([
            promise1,
            promise2
          ])
          answer = promisesResults[0]
        }

        this.dataSending = false

        if (answer) {
          this.$store.commit('setTickDetails', answer)
          this.$store.commit('setTickType', tickType)
          this.$store.commit('setResult', answer)
          this.$router.push('results')
        } else {
          this.showError(JSON.stringify(res))
          this.reshoot()
        }
      } catch (e) {
        // this.showError(e)
        this.showError(e.message)
        this.$router.push('/')
      }
    },
    showError (msg) {
      this.$store.commit('setErrorModal', {
        enabled: true,
        message: msg
      })
    }
  },
  computed: {
    ...mapState([
      'selectedWorker',
      'tickPoint'
    ]),
    ...mapGetters(['getFullName'])
  },
  mounted () {
    camService = new CamService(this.$refs.bgVideo, {
      onCameraReady: () => {
        this.videoStreamReady = true
      }
    })
    camService.initialize()

    if (process.env.DEV) {
      document.onkeyup = async function (e) {
        if (e.which === 57) {
          const imageBlob = await camService.getPicture()
          const objectURL = URL.createObjectURL(imageBlob)
          window.open(objectURL, '_blank')
          return false
        }
      }
    }
  },
  beforeDestroy () {
    this.$store.commit('setFirstPicsPaused', true)
  }
}
</script>
<style lang="stylus">
body
  overflow-x: hidden
.height-limited
  max-height: calc(100vh)
.worker-title
  font-size: 20px
  font-weight: bold
.mirror
  flex-grow: 1
.spinner
  z-index: 100
</style>
